import { useState, useEffect } from "react";
import { BindingDataDto, ParsedRowType } from "../../../types";

const INDEX_EMPTY = "0";
const INDEX_DEFAULT = "-1";

export const useBindingManagement = (
  open: boolean,
  parsed: ParsedRowType[],
  bindingRules?: BindingDataDto[]
) => {
  const [showBindings, setShowBindings] = useState(false);
  const [bindings, setBindings] = useState<BindingDataDto[]>([]);

  const getIndexLabel = (id: string, getDisplayIndex: (parsed: ParsedRowType[], itemId?: string | null, isParameterFlag?: boolean) => string) => {
    if (id === INDEX_DEFAULT) return "-Seçiniz-";
    if (id === INDEX_EMPTY) return "-Boş-";
    return getDisplayIndex(parsed, id, true);
  };
  const getIndexValueLabel = (itemId?: string | null): string => {
    if (!itemId) return "";
    if (itemId === INDEX_DEFAULT) return "";
    if (itemId === INDEX_EMPTY) return "";

    const item = parsed.find(p => p.id === itemId);
    if (!item) return "";

    return (
      item.textValueConvert?.trim() || ""
    );
  };
  const getBindingPreview = (row: BindingDataDto) => {
    if (!row.bindingParseData.length) return "";

    const values = row.bindingParseData.map((p) => {
      if (!p.itemId) return "";
      if (p.itemId === INDEX_EMPTY || p.itemId === INDEX_DEFAULT) return "";
      return getIndexValueLabel(p.itemId);
    });

    return values.join(`${row.separator}`);
  };
  const loadBindingRules = (parsedArray: ParsedRowType[], bindingRules?: BindingDataDto[]) => {
    if (!bindingRules || bindingRules.length === 0) return;

    const apiIdToUuidMap = new Map<string, string>();
    parsedArray.forEach(p => {
      if (p.id != null) {
        apiIdToUuidMap.set(p.id.toString(), p.id);
      }
    });

    setBindings(
      bindingRules.map(binding => ({
        ...binding,
        bindingParseData: binding.bindingParseData.map(bpd => {
          const resolvedItemId = bpd.itemId !== undefined
            ? bpd.itemId
            : bpd.parseId != null
              ? apiIdToUuidMap.get(bpd.parseId.toString()) ?? null
              : null;

          return {
            ...bpd,
            itemId: resolvedItemId
          };
        })
      }))
    );
  };

  const buildBindingRules = (): BindingDataDto[] =>
    bindings.map(b => ({
      name: b.name,
      description: b.description ?? "",
      separator: b.separator ?? ",",
      bindingParseData: b.bindingParseData.map(p => ({
        itemId: p.itemId ?? null,
        order: p.order,
      })),
    }));

  const resetBindings = () => {
    setBindings([]);
    setShowBindings(false);
  };

  useEffect(() => {
    if (!open) {
      resetBindings();
      return;
    }

    if (!parsed.length) return;
    if (!bindings || bindings.length === 0) return;

    const parsedIdSet = new Set(parsed.map(p => p.id));

    setBindings(prev => {
      return prev.map(binding => ({
        ...binding,
        bindingParseData: binding.bindingParseData.map(bpd => {
          const itemId = bpd.itemId;
          if (itemId === INDEX_DEFAULT || itemId === INDEX_EMPTY) {
            return bpd;
          }
          if (itemId && parsedIdSet.has(itemId)) {
            return bpd;
          }
          return { ...bpd, itemId: itemId == null ? INDEX_EMPTY : INDEX_DEFAULT };
        })
      }));
    });
  }, [parsed, open]);

  return {
    showBindings,
    bindings,
    setShowBindings,
    setBindings,
    getIndexLabel,
    getBindingPreview,
    loadBindingRules,
    buildBindingRules,
    resetBindings,
  };
};
